import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AdaptiveFormShell } from '@/shared/ui/components/form';
import { type SearchAutocompleteOption } from '@/shared/ui/components/form/SearchAutocomplete';
import { useVisitDrawerForm } from './useVisitDrawerForm';
import { useAgencyProperties } from '../../hooks/useAgencyProperties';
import { useAgencyClients } from '../../hooks/useAgencyClients';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { VisitsService } from '@services/requests/housien-services/VisitsService';
import { getOccupiedSlots } from '@pages/authenticated/agency-pages/visits/visit-time-conflict';
import { VisitDrawerSubmitButton } from './SubmitButton';
import { VisitDrawerFields } from './VisitDrawerFields';
import { useIsSoloBroker } from '@/shared/hooks/use-is-solo-broker';
import { isDevFormBridgeEnabled } from '@/shared/utils/dev-form-bridge';

export interface VisitDrawerProps {
  open: boolean;
  onClose: () => void;
  agencyId: string;
  userEmail: string;
  editingVisit?: any;
  /**
   * Broker options. Defaults to the current agency's members, so a caller that
   * omits it still gets a populated broker select instead of an empty one.
   */
  members?: Array<{ email: string; first_name: string; last_name: string }>;
  /** Prefill client when creating from a client profile. */
  initialClientId?: string;
  /**
   * Prefill the attendance when creating from a deal's detail screen.
   * Routes the create through `POST /deals/{id}/visits` so the visit
   * lands under that attendance instead of opening a new one.
   */
  initialDealId?: string;
  /** Prefill the property when creating from a deal that already has one. */
  initialPropertyId?: string;
  onSuccess?: () => void;
}

/**
 * Adaptive create/edit shell for agency property visits. Loads property, client,
 * and broker options, tracks occupied time slots, and wires the form to the
 * submit button.
 *
 * @param props - Open state, close/success callbacks, agency and user context,
 *   optional visit being edited, member list, and an optional prefilled client.
 * @returns The visit drawer with its form fields and submit footer.
 */
export function VisitDrawer({
  open,
  onClose,
  agencyId,
  userEmail,
  editingVisit,
  members,
  initialClientId,
  initialDealId,
  initialPropertyId,
  onSuccess,
}: VisitDrawerProps) {
  const { t } = useTranslation('features/visits');
  const agencyMembers = useAgency((state) => state.agency?.members);
  const isSoloBroker = useIsSoloBroker();
  const { properties } = useAgencyProperties(open ? agencyId : undefined);
  const { clients } = useAgencyClients(open ? agencyId : undefined);
  const [occupiedSlots, setOccupiedSlots] = useState<Set<string>>(new Set());

  const { form, isSubmitting, hasValidationErrors, handleSubmit, isEditMode } = useVisitDrawerForm({
    agencyId,
    userEmail,
    editingVisit,
    initialClientId,
    open,
    initialDealId,
    initialPropertyId,
    onSuccess: () => {
      onSuccess?.();
      onClose();
    },
  });

  const watchedValues = form.watch();

  const propertyOptions: SearchAutocompleteOption[] = useMemo(
    () => properties.map((p) => ({ id: p.id, label: p.label, sublabel: p.sublabel })),
    [properties],
  );

  const clientOptions: SearchAutocompleteOption[] = useMemo(
    () => clients.map((c) => ({ id: c.id, label: c.label, sublabel: c.sublabel })),
    [clients],
  );

  const selectedProperty = useMemo(
    () => propertyOptions.find((p) => p.id === watchedValues.propertyId) ?? null,
    [propertyOptions, watchedValues.propertyId],
  );

  const selectedClient = useMemo(
    () => clientOptions.find((c) => c.id === watchedValues.clientId) ?? null,
    [clientOptions, watchedValues.clientId],
  );

  const brokerOptions: SearchAutocompleteOption[] = useMemo(
    () => (members?.length ? members : agencyMembers ?? []).map((m) => ({
      id: m.email,
      label: `${m.first_name} ${m.last_name}`.trim(),
      sublabel: m.email,
    })),
    [members, agencyMembers],
  );

  useEffect(() => {
    if (!agencyId || !watchedValues.visitDate || !open) {
      setOccupiedSlots(new Set());
      return;
    }
    let cancelled = false;
    VisitsService.getPropertyVisits(agencyId, 1, 100)
      .then((result) => {
        if (cancelled) return;
        const visits = Array.isArray(result.data) ? result.data : [];
        setOccupiedSlots(getOccupiedSlots(visits, watchedValues.visitDate, editingVisit?.visit_id));
      })
      .catch(() => { if (!cancelled) setOccupiedSlots(new Set()); });
    return () => { cancelled = true; };
  }, [agencyId, watchedValues.visitDate, open, editingVisit?.visit_id]);

  useEffect(() => {
    if (open && isDevFormBridgeEnabled()) {
      (window as any).__DEV_VISIT_FORM__ = {
        setValue: form.setValue,
        getValues: form.getValues,
        trigger: form.trigger,
        submit: handleSubmit,
      };
    }
    return () => { (window as any).__DEV_VISIT_FORM__ = undefined; };
  }, [open, form, handleSubmit]);

  const buttonLabel = isEditMode ? t('updateVisit') : t('confirmVisit');

  return (
    <AdaptiveFormShell
      open={open}
      onClose={onClose}
      title={isEditMode ? t('editVisit') : t('newVisit')}
      density="compact"
      footer={
        <VisitDrawerSubmitButton
          label={buttonLabel}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
        />
      }
    >
      <VisitDrawerFields
        form={form}
        agencyId={agencyId}
        propertyOptions={propertyOptions}
        clientOptions={clientOptions}
        brokerOptions={brokerOptions}
        showBrokerField={!isSoloBroker}
        occupiedSlots={occupiedSlots}
        selectedProperty={selectedProperty}
        selectedClient={selectedClient}
        visitDate={watchedValues.visitDate}
        visitTime={watchedValues.visitTime}
        hasValidationErrors={hasValidationErrors}
      />
    </AdaptiveFormShell>
  );
}
